import { deepAssign } from "../../../dojo/core/lang";
import global from "@dojo/framework/shim/global";
import { assign } from "@dojo/framework/shim/object";
import WeakMap from "@dojo/framework/shim/WeakMap";
import {
	CANVAS_DELAY_MS,
	MOVEMENT_THRESHOLD_PX,
} from "../../../Constants";
import PageHelper from "../../../store/PageHelper";
import { getObjectCenter, radian, radToDeg, truncate } from "../../../store/reducers/util";
import { IRotateResult } from "../../interfaces/meta/RotateResult";
import { IOffset } from "../../interfaces/model/dataStructures/index";
import { Keys } from "../../interfaces/model/enumerations/index";
import { IPageObjectTypes } from "../../interfaces/model/enumerations/PageObjectTypes";
import { PageObjectType } from "../../interfaces/model/Types";
import { isSnapToGridDisabled } from "../../keyUtil";
import { getZoomValue, stateConstants } from "../../selectors/index";
import { adjustPositionWithinEditor, setUserIsDragging } from "../../util";
import { getStore } from "canvas/store/ReduxRegistry";

/**
 * Drag result properties
 */
export interface IDragResults {
    /**
     * Represents current position of pointer on canvas
     */
    current?: IPosition;

    /**
     * The movement of pointer during the duration of the drag state
     */
    delta: IPosition;

    /**
     * Is the DOM node currently in a drag state
     */
    isDragging: boolean;

    /**
     * Key code
     */
    keyData?: IKeyData;

    /**
     * Position of mouse when drag was initiated
     */
    mouseStart: IPositionMatrix;

    /**
     * Lock X or Y axis
     */
    lock: ILockMatrix;

    /** Flag to indicate that the pointer is out of editor */
    outOfEditor?: boolean;

    /**
     * Represent current position of pointer in canvas
     */
    positionInCanvas: IPosition;

    /**
     * A matrix of position that represent the start position for the current drag interaction
     */
    start?: IPositionMatrix;

    /**
     * Meant to represent the current time in seconds
     */
    timer: number;
}

/**
 * Key data properties
 */
export interface IKeyData {
    altKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    shiftKey: boolean;
    escKey: boolean;
}

/**
 * Node data properties
 */
interface INodeData {
    dragResults: IDragResults;
    invalidate: () => void;
    last: IPositionMatrix;
    start: IPositionMatrix;
    keyCode: number;
    current: IPosition;
    positionInCanvas: IPosition;
}

/**
 * An x/y position structure
 */
export interface IPosition {
    x: number;
    y: number;
}

/**
 * An x/y position structure
 */
export interface ILockMatrix {
    x: boolean;
    y: boolean;
}

/**
 * A matrix of x/y positions
 */
export interface IPositionMatrix {
    /**
     * Client x/y position
     */
    client: IPosition;

    /**
     * Offset x/y position
     */
    offset: IPosition;

    /**
     * Page x/y position
     */
    page: IPosition;

    /**
     * Screen x/y position
     */
    screen: IPosition;
}

/**
 * Create node data
 * @param invalidate Function call back
 * @return { INodeData }
 */
function createNodeData(invalidate: () => void): INodeData {
	return {
		current: createCurrentPosition(),
		dragResults: deepAssign({
			lock: createLockMatrix(),
			mouseStart: createPositionMatrix(),
			positionInCanvas: createCurrentPosition(),
			timer: 0,
		}, emptyResults),
		invalidate,
		keyCode: 0,
		last: createPositionMatrix(),
		positionInCanvas: createCurrentPosition(),
		start: createPositionMatrix(),
	};
}

/**
 * Creates a lock matrix initialized to false
 * @return { IPosition }
 */
function createLockMatrix(): ILockMatrix {
	return { x: false, y: false };
}

/**
 * Creates an empty position
 * @return { IPosition }
 */
export function createPosition(): IPosition {
	return { x: 0, y: 0 };
}

/**
 * Create an empty IPosition matrix
 * @return { IPositionMatrix }
 */
function createPositionMatrix(): IPositionMatrix {
	return {
		client: { x: 0, y: 0 },
		offset: { x: 0, y: 0 },
		page: { x: 0, y: 0 },
		screen: { x: 0, y: 0 },
	};
}

/**
 * Create current an empty IPosition matrix
 * @return { IPosition }
 */
function createCurrentPosition(): IPosition {
	return {
		x: 0,
		y: 0,
	};
}

/**
 * A frozen empty result object, frozen to ensure that no one downstream modifies it
 */
export const emptyResults = Object.freeze({
	delta: Object.freeze(createPosition()),
	isDragging: false,
	lock: createLockMatrix(),
	mouseStart: createPositionMatrix(),
	positionInCanvas: createCurrentPosition(),
	timer: 0,
});

/**
 * Get the state to get the node for the canvas container
 * @return canvasContainer
 */
function _getCanvasContainerEl(): HTMLElement {
	const state = getStore()
		.getState() as any;
	return state.canvasEditor.canvasContainerEl || document.body;
}

/**
 * Return the x/y position matrix for an event
 * @param event The pointer event
 * @return { IPositionMatrix }
 */
function getPositionMatrix(event: PointerEvent): IPositionMatrix {
	return {
		client: {
			x: event.clientX,
			y: event.clientY,
		},
		offset: {
			x: event.offsetX,
			y: event.offsetY,
		},
		page: {
			x: event.pageX,
			y: event.pageY,
		},
		screen: {
			x: event.screenX,
			y: event.screenY,
		},
	};
}

/**
 * Get key data
 * @param event The pointer event
 * @return { IKeyData }
 */
function getKeyData(event: PointerEvent): IKeyData {
	return {
		altKey: event.altKey,
		ctrlKey: event.ctrlKey,
		escKey: false,
		metaKey: event.metaKey,
		shiftKey: event.shiftKey,
	};
}

/**
 * Return the delta position between two positions
 * @param start The first position
 * @param current The second position
 * @return { IPosition }
 */
function getDelta(start: IPositionMatrix, current: IPositionMatrix, position: ClientRect): IPosition {
	const startPosition = adjustPositionWithinEditor(start.client, position, false);
	const currentPosition = adjustPositionWithinEditor(current.client, position, false);
	return {
		x: currentPosition.x - startPosition.x,
		y: currentPosition.y - startPosition.y,
	};
}

/**
 * Reject movement if the movement was slight and a certain time limit was not met
 * @param event - mouse position after dragStart
 * @param mouseStart - mouse position on dragStart
 * @param timer - timer starting on dragStart
 * @return { Boolean }
 */
function rejectMovement(event: PointerEvent, mouseStart: IPositionMatrix, timer: number): boolean {
	// if there was little movement in little time, ignore it
	if (((Math.abs(event.clientX - mouseStart.client.x) < MOVEMENT_THRESHOLD_PX) &&
        Math.abs(event.clientY - mouseStart.client.y) < MOVEMENT_THRESHOLD_PX) &&
        (Date.now() - timer < CANVAS_DELAY_MS)) {
		return true;
	}
	return false;
}

/**
 * Adding key events in Meta Drag
 * Meta drag result does not provide the key events information so adding it by customizing Drag meta
 * Reference: https://github.com/dojo/widget-core/blob/master/src/meta/Drag.ts
 */
class CsiDragController {
	private _nodeMap = new WeakMap<HTMLElement, INodeData>();
	private _dragging: HTMLElement | undefined = undefined;

	// Used to give a type to the shape being dragged
	private shapeType: string | undefined = undefined;

	constructor() {
		const win: Window = global.window;
		win.addEventListener("pointerdown", this._onDragStart);
		// Use capture phase, to determine the right node target, as it will be top down versus bottom up
		win.addEventListener("pointermove", this._onDrag, true);
		win.addEventListener("pointerup", this._onDragStop, true);
		win.addEventListener("keydown", this._onKeyDown, false);
	}

	public getRotation(node: HTMLElement, pageObject: PageObjectType, invalidate: () => void): IRotateResult {
		const rotationResult: IRotateResult = {
			isRotating: false,
			rotation: 0,
		};
		if (!node) {
			return rotationResult;
		}
		const dragResult = this.get(node, invalidate);
		rotationResult.isRotating = dragResult.isDragging;

		if ((dragResult.delta.x || dragResult.delta.y)) {
			// Center of selected object
			const centerPt: IOffset = getObjectCenter(pageObject.spPr);

			// Get radian between two points
			const r = radian(centerPt, dragResult.current as IOffset);

			// If computed r is not valid then return rotation result with isRotating as false and rotation as zero
			if (!r) {
				rotationResult.isRotating = false;
				return rotationResult;
			}
			// Convert radian angle to degree
			let d = radToDeg(r) % 360;
			// Truncate number to precision 1
			d = truncate(d, 1);
			rotationResult.rotation = d;
			// this.properties.rotateObjects(d);
		}
		return rotationResult;
	}

	/**
     * Remove the node for drag listening purpose. This should be called when widget is removed. The most suitable place
     * is to call this from widget's onDetach() method
     * @param node The node to remove for drag listening purpose.
     */
	public remove(node: HTMLElement) {
		const { _nodeMap } = this;
		// IF we have the node in _nodeMap then remove it
		if (_nodeMap.has(node)) {
			_nodeMap.delete(node);
		}
		return;
	}

	/**
     * Get drag results
     * @param node { HTMLElement }
     * @return { IDragResults }
     */
	public get(node: HTMLElement, invalidate: () => void, type?: string): IDragResults {
		const { _nodeMap } = this;
		// first time we see a node, we will initialize its state and properties
		if (!_nodeMap.has(node)) {
			_nodeMap.set(node, createNodeData(invalidate));
			return emptyResults;
		}
		this.shapeType = type;
		const state = _nodeMap.get(node)!;
		// shallow "clone" the results, so no downstream manipulation can occur
		const dragResults = assign({}, state.dragResults);
		// we are offering up an accurate delta, so we need to take the last event position and move it to the start so
		// that our deltas are calculated from the last time they are read
		state.start = state.last;
		// reset the delta after we have read, as any future reads should have an empty delta
		state.dragResults.delta = createPosition();
		// clear the start state
		delete state.dragResults.start;

		return dragResults;
	}

	/**
     * Get data
     * @param target { HTMLElement } Target element
     */
	private _getData(target: HTMLElement): { state: INodeData; target: HTMLElement } | undefined {
		if (this._nodeMap.has(target)) {
			return { state: this._nodeMap.get(target)!, target };
		}
		// TODO -> this is needed on modern non-IE browsers for pageObject movement. Figure out why.
		const parent = (target.parentElement || target.parentNode) as HTMLElement;
		if (parent) {
			return this._getData(parent);
		}
	}

	/**
     * Return snapped delta value
     * @param  {INodeData} state
     * @returns IPosition
     */
	private getSnappedDelta(state: INodeData): IPosition {
		const dragResults = state.dragResults;
		const current = dragResults.current;
		const gridInc = stateConstants().gridInc;
		let x1 = 0;
		let x2 = 0;
		let y1 = 0;
		let y2 = 0;
		if (state && dragResults && current) {
			x1 = Math.floor((current.x - dragResults.delta.x) / gridInc);
			y1 = Math.floor((current.y - dragResults.delta.y) / gridInc);
			x2 = Math.floor(current.x / gridInc);
			y2 = Math.floor(current.y / gridInc);
		}
		return { x: (x2 - x1) * gridInc, y: (y2 - y1) * gridInc };
	}

	/**
     * Drag start event
     * @param event { PointerEvent } Pointer event
     */
	private _onDragStart = (event: PointerEvent) => {
		const { _dragging } = this;
		if (!event.isPrimary && _dragging) {
			// we have a second touch going on here, while we are dragging, so we aren't really dragging, so we
			// will close this down
			const state = this._nodeMap.get(_dragging)!;
			state.dragResults.isDragging = false;
			state.invalidate();
			this._dragging = undefined;
			return;
		}
		if (event.button !== 0) {
			// it isn't the primary button that is being clicked, so we will ignore this
			return;
		}
		const data = this._getData(event.target as HTMLElement);
		if (data) {
			const { state, target } = data;
			this._dragging = target;
			const dragResults = state.dragResults;

			// get time for dragStart to elimiate micromovements later
			dragResults.timer = Date.now();

			// set lock matrix to false
			dragResults.lock = createLockMatrix();

			state.last = state.start = dragResults.mouseStart = getPositionMatrix(event);
			dragResults.delta = createPosition();
			dragResults.current = createCurrentPosition();
			dragResults.start = deepAssign({}, state.start);
			dragResults.isDragging = true;
			state.keyCode = 0;
			// get current offset of cursor on canvas
			const mx = dragResults.start.client.x;
			const my = dragResults.start.client.y;

			const canvasContainerBound = _getCanvasContainerEl()
				.getBoundingClientRect();

			dragResults.positionInCanvas = {
				x: getZoomValue(mx - canvasContainerBound.left),
				y: getZoomValue(my - canvasContainerBound.top),
			};

			dragResults.keyData = getKeyData(event);
			state.invalidate();
		} // else, we are ignoring the event
	};

	/**
     * Drag event
     * @param event { PointerEvent } Pointer event
     */
	private _onDrag = (event: PointerEvent) => {
		const { _dragging } = this;
		const  _dragMoveShape = this.shapeType;
		if (!_dragging) {
			return;
		}
		// Prevent too many undo operations on drag (C8-10199).
		setUserIsDragging(true);
		// state cannot be unset, using ! operator
		const state = this._nodeMap.get(_dragging)!;
		if (!state) {
			return;
		}

		const dragResults = state.dragResults;

		if (rejectMovement(event, dragResults.mouseStart, dragResults.timer)) {
			return;
		}

		state.last = getPositionMatrix(event);
		dragResults.keyData = getKeyData(event);

		if (!dragResults.start) {
			dragResults.start = deepAssign({}, state.start);
		}

		// get current offset of cursor on canvas
		const mx = dragResults.start.client.x;
		const my = dragResults.start.client.y;

		if (!dragResults.current) {
			dragResults.current = deepAssign({}, state.current);
		}

		const canvasContainerBound = _getCanvasContainerEl()
			.getBoundingClientRect();

		dragResults.current.x = getZoomValue(mx - canvasContainerBound.left);
		dragResults.current.y = getZoomValue(my - canvasContainerBound.top);

		const positionInCanvas = deepAssign({}, dragResults.current);
		dragResults.positionInCanvas = positionInCanvas;

		// C8-20290 - Snap to Axis if Shift is pressed
		const { keyData } = dragResults;
		// Assure that a connection shape is not being drawn nor rect handles are being moved while dragging
		if (keyData && keyData.shiftKey &&
            _dragging.nodeName !== "rect" &&
            _dragMoveShape !== IPageObjectTypes.CONNECTION_SHAPE) {
			if (Math.abs(event.clientX - dragResults.mouseStart.client.x) <
                Math.abs(event.clientY - dragResults.mouseStart.client.y)) {
				dragResults.lock.x = true;
				dragResults.lock.y = false;
			} else {
				dragResults.lock.x = false;
				dragResults.lock.y = true;
			}
			if (dragResults.lock.x === true) {
				state.last.client.x = dragResults.mouseStart.client.x;
			} else if (dragResults.lock.y === true) {
				state.last.client.y = dragResults.mouseStart.client.y;
			}
		}

		dragResults.delta = getDelta(state.start, state.last, document.body.getBoundingClientRect());

		if (stateConstants().isSnapToGrid ||
            stateConstants().snapXToGuides ||
            stateConstants().snapYToGuides) {
			const snapDelta = this.getSnappedDelta(state);
			if (stateConstants().isSnapToGrid && !isSnapToGridDisabled(keyData)) {
				dragResults.delta.x = snapDelta.x;
				dragResults.delta.y = snapDelta.y;
			}
			if (stateConstants().snapXToGuides) {
				dragResults.delta.x = snapDelta.x;
			}
			if (stateConstants().snapYToGuides) {
				dragResults.delta.y = snapDelta.y;
			}
		}

		dragResults.delta.x = getZoomValue(dragResults.delta.x);
		dragResults.delta.y = getZoomValue(dragResults.delta.y);

		// if event pointer x and y fall in rage of canvas-editor-root boundaries then set outOfEditor to false, true
		// otherwise. Let the consumer decide strategy based on this fact.
		if (event.clientX < canvasContainerBound.left || event.clientX > canvasContainerBound.right ||
            event.clientY < canvasContainerBound.top || event.clientY > canvasContainerBound.bottom) {
			dragResults.outOfEditor = true;
		} else {
			dragResults.outOfEditor = false;
		}

		state.invalidate();
	};

	/**
     * Drag stop event
     * @param event { PointerEvent } Pointer event
     */
	private _onDragStop = (event: PointerEvent) => {
		const { _dragging } = this;
		if (!_dragging) {
			return;
		}
		// state cannot be unset, using ! operator
		const state = this._nodeMap.get(_dragging);
		if (state) {
			const { dragResults } = state;

			// expand editor to match dimensions of absolutely positioned content
			PageHelper.expandEditor(PageHelper.getEditorDimensions(), false, PageHelper.isDefaultCanvasEditMode());

			state.last = getPositionMatrix(event);

			if (!dragResults.start) {
				dragResults.start = deepAssign({}, state.start);
			}

			if (dragResults.lock.x) {
				state.last.client.x = dragResults.mouseStart.client.x;
			} else if (dragResults.lock.y) {
				state.last.client.y = dragResults.mouseStart.client.y;
			}

			const canvasContainerBound = _getCanvasContainerEl()
				.getBoundingClientRect();

			dragResults.delta = getDelta(state.start, state.last, document.body.getBoundingClientRect());

			state.keyCode = 0;

			dragResults.isDragging = false;

			// if event pointer x and y fall in rage of canvas-editor-root boundaries then set outOfEditor to false,
			// true otherwise. Let the consumer decide strategy based on this fact.
			if (event.clientX < canvasContainerBound.left || event.clientX > canvasContainerBound.right ||
                event.clientY < canvasContainerBound.top || event.clientY > canvasContainerBound.bottom) {
				dragResults.outOfEditor = true;
			} else {
				dragResults.outOfEditor = false;
			}

			state.invalidate();
		}
		this._dragging = undefined;
		// Prevent too many undo operations on drag (C8-10199).
		setUserIsDragging(false);
	};

	/**
     * Key down event
     */
	private _onKeyDown = (event: KeyboardEvent) => {
		const { _dragging } = this;
		if (_dragging) {
			const state = this._nodeMap.get(_dragging)!;
			state.keyCode = event.keyCode;
			const dragResults = state.dragResults;

			// cancel drag with space bar or esc
			if (dragResults.current && [Keys.SPACE_BAR, Keys.ESCAPE].includes(state.keyCode)) {
				dragResults.delta.x = dragResults.delta.y = 0;
				const keyData = dragResults.keyData as IKeyData;
				if (keyData) {
					keyData.escKey = true;
				}
				dragResults.isDragging = false;
				state.invalidate();
				this._dragging = undefined;
			}
		}
	};
}

// export CsiDragController instance
export const controller = new CsiDragController();
